import type {MuteState} from '../audio/mute-state'
import {byId} from './dom'

/**
 * The speaker button in the top-right corner.
 *
 * Writes the shared `MuteState` and nothing else - the sound bank subscribes
 * to that state on its own, so this file knows nothing about audio nodes or
 * the app module's lifecycle. It lives for the whole page, like the
 * attribution link: main.ts reveals it once the consent dialog is answered
 * and never tears it down, which is why there is no dispose here.
 *
 * A useful side effect of being a real tap: the window-level unlock listeners
 * see it, so "start muted, unmute later" unlocks the AudioContext on the
 * unmute tap with no extra code.
 */

/** Mirrors the ids in `index.html`. */
const ELEMENT_IDS = {
  button: 'sound-toggle',
  iconOn: 'sound-toggle-on',
  iconOff: 'sound-toggle-off',
} as const

/** Binds the button to `muteState` and reveals it. */
export const createSoundToggle = (muteState: MuteState): void => {
  const button = byId(ELEMENT_IDS.button, HTMLButtonElement)
  const iconOn = byId(ELEMENT_IDS.iconOn, SVGSVGElement)
  const iconOff = byId(ELEMENT_IDS.iconOff, SVGSVGElement)

  const render = (): void => {
    const {muted} = muteState
    // aria-pressed=true means "mute engaged", matching the fixed "ミュート"
    // label - only the state is dynamic, so a screen reader never hears the
    // label and the state disagree. SVGElement has no `hidden` property; the
    // attribute is written directly and the [hidden] rule applies all the same.
    button.setAttribute('aria-pressed', String(muted))
    iconOn.toggleAttribute('hidden', muted)
    iconOff.toggleAttribute('hidden', !muted)
  }

  button.addEventListener('click', () => muteState.set(!muteState.muted))
  // Rendered through the subscription rather than in the click handler, so the
  // icon stays honest against every writer of the state, not just this button.
  muteState.subscribe(render)
  render()
  button.hidden = false
}
